import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Save, Archive, ArchiveRestore, User, Clock, Sparkles,
  RefreshCw, TrendingUp, History, AlertCircle, Check, Mail, Phone,
  Calendar, CalendarDays, Trash2, Plus, KeyRound, Copy, X, CalendarRange,
} from 'lucide-react';
import { RequestWeekPreview } from './RequestWeekPreview';
import { WeeklyTemplateEditor, SiteScopeBar, type SiteView } from '@/components/WeeklyTemplateEditor';
import {
  useEmployee, useUpdateEmployee, useEmployeeCounters,
  useEmployeeUnavailabilities, useSetUnavailability, useDeleteUnavailability,
  useHolidays, useSeedFrenchHolidays,
  useAbsences, useCreateAbsence, useDeleteAbsence,
  useInviteEmployee, type EmployeeInvite,
  useEmployeeHistory,
  useHourAdjustments, useAddHourAdjustment, useDeleteHourAdjustment,
  useDeleteEmployeePermanent,
  useSites,
  useAppointmentCatalog,
} from '@/api/resources';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { ContractHoursInput } from '@/components/ui/ContractHoursInput';
import type { Employee, Skill, EmployeePosition, ContractType, RotationCycleType, AbsenceType, AppointmentCatalogItem } from '@/types';
import { ABSENCE_META, ABSENCE_TYPES } from '@/lib/absenceTypes';
import { SKILL_LABELS, SKILL_DESCRIPTIONS, SKILL_FAMILIES } from '@/lib/skills';

// Libellés, descriptions et familles : voir `@/lib/skills` (source unique).

const POSITION_LABELS: Record<EmployeePosition, string> = {
  PHARMACIEN: 'Pharmacien·ne',
  ETUDIANT_REMPLACANT_PHARMACIEN: 'Étudiant remplaçant pharmacien',
  PREPARATEUR: 'Préparateur·trice',
  ETUDIANT: 'Étudiant',
  MAGASINIER: 'Magasinier·e',
  MENAGE: 'Ménage',
  AUTRE: 'Autre',
};

const CONTRACT_LABELS: Record<ContractType, string> = {
  CDI: 'CDI', CDD: 'CDD', REMPLACANT: 'Remplaçant',
  INTERIM: 'Intérim', APPRENTISSAGE: 'Apprentissage', STAGE: 'Stage',
};

const AVATAR_COLORS = [
  '#1e3a5f', '#ff6b57', '#6b7a8f', '#8a95a8', '#0891b2',
  '#059669', '#c026d3', '#ea580c', '#65a30d', '#dc2626',
];

type TabId = 'identity' | 'template' | 'availability' | 'skills' | 'rotation' | 'counters' | 'history';

/**
 * Officines d’un salarié partagé, telles que la fiche les renvoie.
 * Lecteur unique, partagé par la synchro du formulaire et par `isDirty` : les
 * deux doivent lire la même chose, sinon le bouton « Enregistrer » ne
 * s’allume jamais alors que la valeur a changé.
 */
function readSiteIds(employee: any): string[] {
  return ((employee?.sites ?? []) as any[])
    .map((s) => s.pharmacyId ?? s.pharmacy?.id)
    .filter(Boolean);
}

export default function EmployeeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: employee, isLoading } = useEmployee(id);
  const updateMut = useUpdateEmployee(id ?? '');
  const inviteMut = useInviteEmployee();
  const deletePermanentMut = useDeleteEmployeePermanent();
  const [tab, setTab] = useState<TabId>('identity');
  /**
   * Multi-site : dans quelle officine on règle ses horaires. On ouvre sur la
   * vue globale — c'est la semaine entière, celle qui répond à « combien
   * d'heures fait-il ». Sans importance pour un salarié mono-site : le bandeau
   * ne s'affiche pas et la vue globale est la seule.
   */
  const [siteView, setSiteView] = useState<SiteView>('ALL');
  const [invite, setInvite] = useState<EmployeeInvite | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);

  async function handleInvite() {
    if (!id) return;
    setInviteError(null);
    try {
      const result = await inviteMut.mutateAsync(id);
      setInvite(result);
    } catch (e) {
      setInviteError((e as Error).message ?? "Échec de la génération du code");
    }
  }

  // State de formulaire (local, enregistré via le bouton "Enregistrer")
  const [form, setForm] = useState<any>(null);

  // Synchroniser le form avec l'employé chargé — UNIQUEMENT au premier chargement
  // ou quand on ouvre un AUTRE salarié. Un refetch en arrière-plan du même salarié
  // (déclenché par d'autres actions de la page : sauvegarde de la semaine type, du
  // calendrier…) ne doit PAS réinitialiser le formulaire, sinon il écrase les
  // modifications non encore enregistrées (ex. couleur choisie mais pas « Enregistrée »).
  // — corrige R9 (la couleur qui « revient » à la base après d'autres manips).
  const syncedEmployeeId = useRef<string | null>(null);
  useEffect(() => {
    if (employee) {
      if (syncedEmployeeId.current === employee.id) return; // même salarié → on garde le form local
      syncedEmployeeId.current = employee.id;
      setForm({
        firstName: employee.firstName,
        lastName: employee.lastName,
        position: employee.position,
        contractType: employee.contractType,
        contractHours: employee.contractHours,
        hireDate: employee.hireDate?.slice(0, 10) ?? '',
        endDate: employee.endDate?.slice(0, 10) ?? '',
        phone: employee.phone ?? '',
        email: employee.email ?? '',
        color: employee.color,
        skills: employee.skills?.map((s) => s.skill) ?? [],
        catalogSkills: employee.catalogSkills?.map((c) => c.catalogItemId) ?? [],
        // Multi-site (M9-3, lot 3B)
        multiSite: (employee as any).multiSite ?? false,
        // Signature de la semaine. Par défaut oui : c'est la règle, la
        // désactiver est l'exception qu'on choisit salarié par salarié.
        signatureRequired: employee.signatureRequired !== false,
        // « Concerné par la fiche navette et le comptage des heures » : l'inverse
        // du titulaire intégré à l'EDT (isOwner, R10).
        counted: !employee.isOwner,
        siteIds: readSiteIds(employee),
        constraints: {
          fixedDayOff: employee.constraints?.fixedDayOff ?? null,
          fixedDayOff2: employee.constraints?.fixedDayOff2 ?? null,
          minStartTime: employee.constraints?.minStartTime ?? '',
          maxEndTime: employee.constraints?.maxEndTime ?? '',
          fixedSchedule: employee.constraints?.fixedSchedule ?? false,
          notes: employee.constraints?.notes ?? '',
        },
        rotationCycle: {
          cycleType: employee.rotationCycle?.cycleType ?? ('NONE' as RotationCycleType),
          cycleWeeks: employee.rotationCycle?.cycleWeeks ?? null,
          startWeek: employee.rotationCycle?.startWeek ?? null,
        },
      });
    }
  }, [employee]);

  const isDirty = useMemo(() => {
    if (!employee || !form) return false;
    const pick = (e: Employee) => ({
      firstName: e.firstName, lastName: e.lastName, position: e.position,
      contractType: e.contractType, contractHours: e.contractHours,
      hireDate: e.hireDate?.slice(0, 10), endDate: e.endDate?.slice(0, 10) ?? '',
      phone: e.phone ?? '', email: e.email ?? '', color: e.color,
    });
    const originalIdentity = JSON.stringify(pick(employee));
    const currentIdentity = JSON.stringify({
      firstName: form.firstName, lastName: form.lastName, position: form.position,
      contractType: form.contractType, contractHours: form.contractHours,
      hireDate: form.hireDate, endDate: form.endDate, phone: form.phone,
      email: form.email, color: form.color,
    });
    if (originalIdentity !== currentIdentity) return true;
    const originalSkills = JSON.stringify([...(employee.skills?.map((s) => s.skill) ?? [])].sort());
    if (originalSkills !== JSON.stringify([...form.skills].sort())) return true;
    const originalCatalog = JSON.stringify(
      [...(employee.catalogSkills?.map((c) => c.catalogItemId) ?? [])].sort()
    );
    if (originalCatalog !== JSON.stringify([...form.catalogSkills].sort())) return true;
    // Le repos fixe n'est pas comparé : la fiche ne le règle pas (voir `handleSave`).
    const originalConstraints = JSON.stringify({
      minStartTime: employee.constraints?.minStartTime ?? '',
      maxEndTime: employee.constraints?.maxEndTime ?? '',
      fixedSchedule: employee.constraints?.fixedSchedule ?? false,
    });
    const currentConstraints = JSON.stringify({
      minStartTime: form.constraints.minStartTime,
      maxEndTime: form.constraints.maxEndTime,
      fixedSchedule: form.constraints.fixedSchedule,
    });
    if (originalConstraints !== currentConstraints) return true;
    const originalCycle = employee.rotationCycle?.cycleType ?? 'NONE';
    if (originalCycle !== form.rotationCycle.cycleType) return true;
    // Multi-site (lot 3B) : « concerné par le multi-site » et les officines
    // cochées partent bien dans `handleSave`, mais restaient absents d’ici. Le
    // bouton affichait donc « Aucun changement » et la case ne s’enregistrait
    // pas — et sans elle, le salarié n’étant pas partagé en base, le sélecteur
    // d’officine par jour de la semaine type ne s’affichait pas non plus.
    if (!!(employee as any).multiSite !== !!form.multiSite) return true;
    if ((employee.signatureRequired !== false) !== !!form.signatureRequired) return true;
    if (!employee.isOwner !== !!form.counted) return true;
    const originalSites = JSON.stringify([...readSiteIds(employee)].sort());
    if (originalSites !== JSON.stringify([...(form.siteIds ?? [])].sort())) return true;
    return false;
  }, [employee, form]);

  async function handleSave() {
    if (!form) return;
    // Le repos fixe ne part PAS d'ici : aucun champ de la fiche ne le règle
    // (c'est « Changer les horaires »), et le formulaire n'en garde que la
    // valeur lue à l'ouverture. Enregistrer la semaine type peut le lever
    // entre-temps — le renvoyer ici le reposait en silence, et le jour
    // redevenait vide au planning.
    const { fixedDayOff: _fixedDayOff, fixedDayOff2: _fixedDayOff2, ...constraints } = form.constraints;
    await updateMut.mutateAsync({
      firstName: form.firstName,
      lastName: form.lastName,
      position: form.position,
      contractType: form.contractType,
      // Pas décompté → ses heures ne partent pas : un titulaire en a 0, que le
      // serveur refuse, et celles d'un salarié décoché restent intactes pour
      // le jour où on le recoche.
      contractHours: form.counted ? form.contractHours : undefined,
      hireDate: form.hireDate,
      endDate: form.endDate || null,
      phone: form.phone || null,
      email: form.email || null,
      color: form.color,
      skills: form.skills,
      catalogSkills: form.catalogSkills,
      constraints,
      rotationCycle: form.rotationCycle.cycleType !== 'NONE' ? form.rotationCycle : { cycleType: 'NONE' },
      // Multi-site (lot 3B) : décocher « concerné » vide aussi les officines,
      // sinon le salarié resterait rattaché à un site sans y être déclaré.
      multiSite: !!form.multiSite,
      siteIds: form.multiSite ? (form.siteIds ?? []) : [],
      signatureRequired: !!form.signatureRequired,
      isOwner: !form.counted,
    } as any);
  }

  async function handleArchive() {
    if (!employee) return;
    const confirmMsg = employee.archived
      ? 'Réactiver ce salarié ? Il réapparaîtra dans la liste et le planning.'
      : `Archiver ${employee.firstName} ${employee.lastName} ? Le salarié sera masqué du planning mais ses données seront conservées.`;
    if (!window.confirm(confirmMsg)) return;
    await updateMut.mutateAsync({ archived: !employee.archived } as any);
  }

  // Suppression DÉFINITIVE (R12) — double confirmation vu l'irréversibilité.
  async function handleDeletePermanent() {
    if (!employee) return;
    const name = `${employee.firstName} ${employee.lastName}`;
    if (!window.confirm(
      `⚠️ Supprimer DÉFINITIVEMENT ${name} ?\n\nTout est effacé et IRRÉVERSIBLE : planning, absences, compteurs, lignes de fiche navette passées et son compte de connexion.\n\nPour conserver l'historique, utilisez plutôt « Archiver ».`
    )) return;
    if (!window.confirm(`Confirmer la suppression définitive de ${name} ? Cette action est irréversible.`)) return;
    await deletePermanentMut.mutateAsync(employee.id);
    navigate('/employees');
  }

  // Remis dans le décompte sans heures dues (titulaire créé comme tel : 0 h),
  // chaque heure travaillée passerait en heure sup — le serveur refuse.
  const missingHours = !!form?.counted && !(Number(form.contractHours) > 0);

  if (isLoading || !employee || !form) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sm text-slate-500">Chargement du salarié…</div>
      </div>
    );
  }

  const tabs: { id: TabId; label: string; icon: typeof User }[] = [
    { id: 'identity', label: 'Identité', icon: User },
    { id: 'template', label: 'Horaires & contraintes', icon: Clock },
    { id: 'availability', label: 'Disponibilités', icon: CalendarDays },
    { id: 'skills', label: 'Compétences', icon: Sparkles },
    { id: 'rotation', label: 'Cycle', icon: RefreshCw },
    // Un titulaire n'est pas décompté → pas d'onglet Compteurs (R10).
    ...(employee.isOwner ? [] : [{ id: 'counters' as TabId, label: 'Compteurs', icon: TrendingUp }]),
    { id: 'history', label: 'Historique', icon: History },
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-slate-200 px-8 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/employees')} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-600" title="Retour à la liste">
              <ChevronLeft size={18} />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full flex items-center justify-center text-white text-[13px] font-semibold flex-shrink-0" style={{ backgroundColor: form.color }}>
                {form.firstName[0]}{form.lastName[0]}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-[22px] font-semibold tracking-tight leading-tight">
                    {form.firstName} {form.lastName}
                  </h1>
                  <Chip variant={employee.archived ? 'slate' : employee.isOwner ? 'primary' : 'green'} size="sm">
                    {employee.archived ? 'Archivé' : employee.isOwner ? 'Titulaire' : CONTRACT_LABELS[form.contractType as ContractType]}
                  </Chip>
                </div>
                <div className="text-[11.5px] text-slate-500 mt-0.5">
                  {POSITION_LABELS[form.position as EmployeePosition]}
                  {employee.isOwner ? " · intégré à l'EDT, non décompté" : ` · ${form.contractHours}h/semaine`}
                  {employee.scheduleMode === 'VARIABLE' && (
                    <span className="ml-1.5 text-primary font-medium">· horaires variables</span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {!employee.archived && (
              <Button variant="default" icon={KeyRound} size="sm" loading={inviteMut.isPending} onClick={handleInvite}>
                Inviter
              </Button>
            )}
            <Button variant="default" icon={employee.archived ? ArchiveRestore : Archive} size="sm" onClick={handleArchive}>
              {employee.archived ? 'Réactiver' : 'Archiver'}
            </Button>
            <Button variant="danger" icon={Trash2} size="sm" loading={deletePermanentMut.isPending} onClick={handleDeletePermanent}>
              Supprimer
            </Button>
            <Button variant="primary" icon={Save} loading={updateMut.isPending} disabled={!isDirty || missingHours} onClick={handleSave}>
              {isDirty ? 'Enregistrer les changements' : 'Aucun changement'}
            </Button>
          </div>
        </div>

        <div className="flex gap-0.5 -mb-4 mt-4">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-3.5 py-2.5 text-[12.5px] font-medium border-b-2 transition-colors ${
                  tab === t.id ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-900'
                }`}
              >
                <Icon size={13} />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {updateMut.error && (
        <div className="bg-danger-bg border-b border-danger/30 px-8 py-2 text-[12px] text-danger flex items-center gap-2">
          <AlertCircle size={13} />
          {(updateMut.error as Error).message}
        </div>
      )}
      {updateMut.isSuccess && !updateMut.isPending && (
        <div className="bg-primary-bg border-b border-primary/30 px-8 py-2 text-[12px] text-primary flex items-center gap-2">
          <Check size={13} />
          Modifications enregistrées
        </div>
      )}
      {inviteError && (
        <div className="bg-danger-bg border-b border-danger/30 px-8 py-2 text-[12px] text-danger flex items-center gap-2">
          <AlertCircle size={13} />
          {inviteError}
        </div>
      )}
      {invite && <InviteModal invite={invite} onClose={() => setInvite(null)} />}

      <div className="flex-1 overflow-auto">
        <div className="max-w-[900px] mx-auto p-8">
          {tab === 'identity' && (
            <IdentityTab
              form={form}
              setForm={setForm}
              isOwner={!form.counted}
              missingHours={missingHours}
              hasAccount={!!employee.hasAccount}
            />
          )}
          {tab === 'template' && (
            <>
              <SiteScopeBar employeeId={employee.id} value={siteView} onChange={setSiteView} />
              <Section title="Horaires & contraintes" description="Jours et horaires par défaut du salarié — base de la génération automatique du planning.">
                <WeeklyTemplateEditor employeeId={employee.id} contractHours={employee.contractHours} siteView={siteView} />
              </Section>
            </>
          )}
          {tab === 'availability' && <AvailabilityTab employee={employee} />}
          {tab === 'skills' && <SkillsTab form={form} setForm={setForm} />}
          {tab === 'rotation' && <RotationTab form={form} setForm={setForm} />}
          {tab === 'counters' && <CountersTab employeeId={employee.id} employee={employee} />}
          {tab === 'history' && <HistoryTab employee={employee} />}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ONGLETS
// ============================================================================

function Section({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="mb-8">
      <div className="mb-3">
        <h2 className="text-[15px] font-semibold text-slate-900">{title}</h2>
        {description && <div className="text-[11.5px] text-slate-500 mt-0.5">{description}</div>}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        {children}
      </div>
    </div>
  );
}

/**
 * Multi-site (M9-3, lot 3B) — « employé concerné ou non par le multi-site ».
 *
 * Ne s'affiche que si le compte gère plusieurs officines : sur une pharmacie
 * seule, la question n'a pas de sens. Une fois « oui » et les officines
 * cochées, la répartition jour par jour se fait dans l'onglet « Semaine type ».
 */
/**
 * Signature de la semaine — l'échappatoire pour le salarié qui a l'application
 * mais ne s'en servira jamais. Sans elle, une seule personne qui ne signe pas
 * tient toute la paie de l'officine.
 *
 * ⚠️ La case ne vaut QUE pour un salarié qui a un compte : sans compte il ne
 * peut pas signer, il est déjà ignoré à la validation. On la montre quand même,
 * désactivée, pour que le titulaire sache que la question est réglée — et pour
 * qu'elle soit déjà au bon endroit le jour où il l'invite.
 */
function SignatureField({ form, set, hasAccount }: {
  form: any;
  set: (k: string, v: any) => void;
  hasAccount: boolean;
}) {
  return (
    <Section
      title="Signature de la semaine"
      description="Attendez-vous que ce salarié signe ses heures dans l'application ?"
    >
      <label className={`flex items-start gap-2.5 ${hasAccount ? 'cursor-pointer' : 'cursor-default opacity-60'}`}>
        <input
          type="checkbox"
          checked={!!form.signatureRequired}
          disabled={!hasAccount}
          onChange={(e) => set('signatureRequired', e.target.checked)}
          className="mt-0.5"
        />
        <span>
          <span className="block text-[13px] text-slate-900">Sa signature est attendue</span>
          <span className="block text-[11px] text-slate-500">
            Décochez pour un salarié qui n'utilisera pas l'application : la validation de la
            semaine ne l'attendra plus, et ne demandera plus de confirmation à cause de lui.
          </span>
        </span>
      </label>

      {!hasAccount && (
        <div className="mt-3 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
          Ce salarié n'a pas encore activé de compte : il n'a aucun moyen de signer, sa semaine se
          valide donc déjà sans lui. Le réglage prendra effet le jour où il activera l'application
          (bouton <strong>Inviter</strong>, en haut).
        </div>
      )}
    </Section>
  );
}

function MultiSiteFields({ form, set }: { form: any; set: (k: string, v: any) => void }) {
  const { data } = useSites();
  if (!data?.multiSite) return null;

  const others = data.sites.filter((s) => s.id !== data.activeSiteId);
  const selected: string[] = form.siteIds ?? [];

  const toggle = (id: string) => {
    set('siteIds', selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);
  };

  return (
    <Section
      title="Multi-site"
      description="Ce salarié travaille-t-il dans plusieurs officines du groupe ?"
    >
      <label className="flex items-start gap-2.5 cursor-pointer">
        <input
          type="checkbox"
          checked={!!form.multiSite}
          onChange={(e) => {
            set('multiSite', e.target.checked);
            if (!e.target.checked) set('siteIds', []);
          }}
          className="mt-0.5"
        />
        <span>
          <span className="block text-[13px] text-slate-900">Concerné par le multi-site</span>
          <span className="block text-[11px] text-slate-500">
            Il apparaîtra dans le planning de chaque officine cochée, et son emploi du temps
            réunira les heures des deux.
          </span>
        </span>
      </label>

      {form.multiSite && (
        <div className="mt-4">
          <label className="label">Autres officines où il travaille</label>
          {others.length === 0 ? (
            <div className="text-[11.5px] text-slate-500">Aucune autre officine dans le groupe.</div>
          ) : (
            <div className="space-y-1.5">
              {others.map((s) => (
                <label key={s.id} className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-slate-200 cursor-pointer hover:border-slate-300">
                  <input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggle(s.id)} />
                  <span className="text-[12.5px] text-slate-800">{s.name}</span>
                  {s.city && <span className="text-[11px] text-slate-400">{s.city}</span>}
                </label>
              ))}
            </div>
          )}
          <div className="mt-3 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
            Sa pharmacie de rattachement — celle qui porte son contrat — est acquise et n'a pas à
            être cochée. La répartition jour par jour se règle dans l'onglet « Semaine type » :
            un jour donné appartient à une seule officine.
          </div>
        </div>
      )}
    </Section>
  );
}

/**
 * « Concerné par la fiche navette et le comptage des heures » — l'inverse du
 * titulaire intégré à l'EDT (isOwner, R10). Jusqu'ici réglable seulement à la
 * création : un titulaire saisi comme salarié (souvent pour le partager entre
 * officines) restait sur la navette et dans la balance, sans retour possible.
 *
 * Toujours affichée, même décochée : c'est le seul chemin pour revenir en arrière.
 */
function CountingField({ form, set }: { form: any; set: (k: string, v: any) => void }) {
  return (
    <Section
      title="Fiche navette et heures"
      description="Ses heures sont-elles à décompter et à transmettre au comptable ?"
    >
      <label className="flex items-start gap-2.5 cursor-pointer">
        <input
          type="checkbox"
          checked={!!form.counted}
          onChange={(e) => set('counted', e.target.checked)}
          className="mt-0.5"
        />
        <span>
          <span className="block text-[13px] text-slate-900">
            Concerné par la fiche navette et le comptage des heures
          </span>
          <span className="block text-[11px] text-slate-500">
            Décochez pour un titulaire : il reste dans le planning, mais n'apparaît plus sur la
            fiche navette ni dans la balance des heures, et ses heures ne sont pas décomptées.
          </span>
        </span>
      </label>
    </Section>
  );
}

function IdentityTab({ form, setForm, isOwner = false, missingHours = false, hasAccount = false }: {
  form: any;
  setForm: (f: any) => void;
  /** Case « fiche navette et comptage des heures » décochée (même pas encore enregistrée). */
  isOwner?: boolean;
  missingHours?: boolean;
  /** A-t-il activé son compte ? Sans compte, la case ne change rien. */
  hasAccount?: boolean;
}) {
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  return (
    <>
      <Section title="État civil" description="Informations de base du salarié.">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="label">Prénom</label>
            <input value={form.firstName} onChange={(e) => set('firstName', e.target.value)} className="input" />
          </div>
          <div>
            <label className="label">Nom</label>
            <input value={form.lastName} onChange={(e) => set('lastName', e.target.value)} className="input" />
          </div>
        </div>

        <label className="label">Couleur dans le planning</label>
        <div className="flex flex-wrap gap-2">
          {AVATAR_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => set('color', c)}
              className={`w-7 h-7 rounded-full transition-all ${form.color === c ? 'ring-2 ring-offset-2 ring-slate-900 scale-110' : 'ring-1 ring-slate-200'}`}
              style={{ backgroundColor: c }}
              title={c}
            />
          ))}
        </div>
      </Section>

      <Section title={isOwner ? 'Poste' : 'Contrat'} description={isOwner ? "Titulaire intégré à l'EDT — pas de contrat ni d'heures décomptées." : undefined}>
        <div className={`grid ${isOwner ? 'grid-cols-1' : 'grid-cols-2'} gap-4 mb-4`}>
          {!isOwner && (
            <div>
              <label className="label">Type de contrat</label>
              <select value={form.contractType} onChange={(e) => set('contractType', e.target.value)} className="input">
                {Object.entries(CONTRACT_LABELS).map(([k, l]) => (
                  <option key={k} value={k}>{l}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="label">Poste</label>
            <select value={form.position} onChange={(e) => set('position', e.target.value)} className="input">
              {Object.entries(POSITION_LABELS).map(([k, l]) => (
                <option key={k} value={k}>{l}</option>
              ))}
            </select>
          </div>
        </div>

        {!isOwner && (
          <div className="mb-4">
            <label className="label">Heures contractuelles (hebdomadaires)</label>
            <ContractHoursInput value={form.contractHours} onChange={(h) => set('contractHours', h)} />
            {missingHours && (
              <div className="mt-1.5 text-[11px] text-danger">
                Indiquez ses heures : il sera décompté sur la fiche navette.
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Date d'embauche</label>
            <input type="date" value={form.hireDate} onChange={(e) => set('hireDate', e.target.value)} className="input" />
          </div>
          <div>
            <label className="label">Date de fin <span className="text-slate-400 font-normal">(facultatif)</span></label>
            <input type="date" value={form.endDate} onChange={(e) => set('endDate', e.target.value)} className="input" />
          </div>
        </div>
      </Section>

      <MultiSiteFields form={form} set={set} />

      <CountingField form={form} set={set} />

      {/* Le titulaire intégré à l EDT ne signe pas : il n est pas décompté. */}
      {!isOwner && <SignatureField form={form} set={set} hasAccount={hasAccount} />}

      <Section title="Contact" description="Pour communications internes et notifications.">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label"><Mail size={11} className="inline mr-1" /> Email</label>
            <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="exemple@domaine.fr" className="input" />
          </div>
          <div>
            <label className="label"><Phone size={11} className="inline mr-1" /> Téléphone</label>
            <input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="06 12 34 56 78" className="input" />
          </div>
        </div>
      </Section>
    </>
  );
}

// ============================================================================
// ONGLET DISPONIBILITÉS — calendrier annuel
// ============================================================================

// Menu Disponibilités : mêmes 11 types + couleurs que partout (source unique).
const REASON_META = ABSENCE_META;
const REASON_ORDER = ABSENCE_TYPES;

const MONTH_NAMES = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];
const WEEKDAY_HEADERS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

function dateKey(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

interface UnavailInfo {
  id: string;
  type: AbsenceType;
  startTime?: string | null;
  endTime?: string | null;
}

// Absence RH (congé, arrêt…) couvrant un jour du calendrier. `isRange` = période
// de plusieurs jours (saisie dans « Absences & congés »).
interface AbsenceDayInfo {
  id: string;
  type: AbsenceType;
  label: string;
  from: string; // yyyy-mm-dd
  to: string;
  isRange: boolean;
}

interface DayPicker {
  dateStr: string;
  label: string;
  existing?: UnavailInfo;
  absence?: AbsenceDayInfo;
  x: number;
  y: number;
}

function AvailabilityTab({ employee }: { employee: Employee }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [picker, setPicker] = useState<DayPicker | null>(null);
  // Aperçu « qui est là » de la semaine du jour cliqué — évite d'aller au
  // planning puis de revenir ici pour saisir l'absence.
  const [weekPreview, setWeekPreview] = useState<string | null>(null);
  // Plage horaire dans le popover : "journée entière" par défaut, sinon plage.
  const [rangeMode, setRangeMode] = useState(false);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('12:00');

  const { data: unavailabilities, isLoading } = useEmployeeUnavailabilities(employee.id, year);
  const { data: holidays } = useHolidays();
  const { data: allAbsences } = useAbsences();
  const setMut = useSetUnavailability(employee.id);
  const deleteMut = useDeleteUnavailability(employee.id);
  const deleteAbsenceMut = useDeleteAbsence();
  const seedMut = useSeedFrenchHolidays();

  // Jours d'absence RH (arrêt, congé…) de l'année → affichés dans le calendrier.
  const absenceMap = useMemo(() => {
    const map = new Map<string, AbsenceDayInfo>();
    (allAbsences ?? [])
      .filter((a) => a.employeeId === employee.id)
      .forEach((a) => {
        const from = a.startDate.slice(0, 10);
        const to = a.endDate.slice(0, 10);
        const info: AbsenceDayInfo = { id: a.id, type: a.type, label: ABSENCE_LABEL[a.type] ?? 'Absence', from, to, isRange: from !== to };
        const d = new Date(from + 'T00:00:00');
        const end = new Date(to + 'T00:00:00');
        while (d <= end) {
          if (d.getFullYear() === year) {
            map.set(dateKey(d.getFullYear(), d.getMonth(), d.getDate()), info);
          }
          d.setDate(d.getDate() + 1);
        }
      });
    return map;
  }, [allAbsences, employee.id, year]);

  // Repos récurrents = jours de la semaine (lun→sam) où le salarié ne travaille
  // JAMAIS dans sa semaine type. Avec le nouveau système, un repos récurrent
  // n'est plus une donnée à part : c'est une journée entière non travaillée.
  const restWeekdays = useMemo(() => {
    const worked = new Set<number>();
    (employee.weeklyTemplates ?? []).forEach((t) => { if (t.works) worked.add(t.dayOfWeek); });
    const rest: number[] = [];
    for (let d = 1; d <= 6; d++) if (!worked.has(d)) rest.push(d);
    return rest;
  }, [employee.weeklyTemplates]);

  // Jours fériés de l'année sélectionnée (map par yyyy-mm-dd)
  const holidayMap = useMemo(() => {
    const map = new Map<string, { name: string; isOpen: boolean }>();
    holidays?.forEach((h: any) => {
      const key = String(h.date).slice(0, 10);
      if (key.startsWith(String(year))) map.set(key, { name: h.name, isOpen: h.isOpen });
    });
    return map;
  }, [holidays, year]);

  // Auto-ajout des jours fériés français si l'année n'en a aucun
  const seededYears = useMemo(() => new Set<number>(), []);
  useEffect(() => {
    if (!holidays) return;
    const hasYear = holidays.some((h: any) => String(h.date).slice(0, 10).startsWith(String(year)));
    if (!hasYear && !seededYears.has(year) && !seedMut.isPending) {
      seededYears.add(year);
      seedMut.mutate(year);
    }
  }, [holidays, year, seedMut, seededYears]);

  const unavailMap = useMemo(() => {
    const map = new Map<string, UnavailInfo>();
    unavailabilities?.forEach((u) => {
      map.set(u.date.slice(0, 10), { id: u.id, type: u.type, startTime: u.startTime, endTime: u.endTime });
    });
    return map;
  }, [unavailabilities]);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    unavailabilities?.forEach((u) => { c[u.type] = (c[u.type] ?? 0) + 1; });
    return c;
  }, [unavailabilities]);

  function openPicker(dateStr: string, label: string, e: React.MouseEvent) {
    const existing = unavailMap.get(dateStr);
    // Pré-remplir le mode plage depuis l'indispo existante
    if (existing?.startTime && existing?.endTime) {
      setRangeMode(true);
      setStartTime(existing.startTime);
      setEndTime(existing.endTime);
    } else {
      setRangeMode(false);
    }
    // Positionne le popover au clic, mais **jamais hors écran** : on borne le haut
    // pour que toute la hauteur estimée tienne dans la fenêtre (sinon la liste de
    // motifs était coupée en bas et, l'overlay étant fixe, impossible de scroller).
    // Filet de sécurité : le popover a aussi maxHeight + scroll interne.
    const absence = absenceMap.get(dateStr);
    // Une absence-période (multi-jours) affiche un popover court (juste « Retirer »).
    const POPOVER_H = absence?.isRange ? 200 : existing ? 560 : 520;
    setPicker({
      dateStr,
      label,
      existing,
      absence,
      x: Math.min(e.clientX, window.innerWidth - 250),
      y: Math.max(12, Math.min(e.clientY, window.innerHeight - 12 - POPOVER_H)),
    });
  }

  async function applyReason(type: AbsenceType) {
    if (!picker) return;
    if (rangeMode && startTime >= endTime) return; // garde-fou
    await setMut.mutateAsync({
      date: picker.dateStr,
      type,
      startTime: rangeMode ? startTime : null,
      endTime: rangeMode ? endTime : null,
    });
    setPicker(null);
  }

  async function removeUnavailability() {
    if (!picker?.existing) return;
    await deleteMut.mutateAsync(picker.existing.id);
    setPicker(null);
  }

  // Supprime toute l'absence-période (congé, arrêt…) depuis n'importe lequel de
  // ses jours sur le calendrier — corrige R8 (avant : supprimable seulement via
  // la liste « Absences & congés » ou en cliquant pile le jour de début).
  async function removeAbsence() {
    if (!picker?.absence) return;
    await deleteAbsenceMut.mutateAsync(picker.absence.id);
    setPicker(null);
  }

  const totalUnavail = unavailabilities?.length ?? 0;

  return (
    <>
      {/* Absences & congés RH (période + type) */}
      <AbsencesSection employeeId={employee.id} year={year} />

      {/* En-tête : année + résumé */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-0.5">
            <button onClick={() => setYear((y) => y - 1)} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600" title="Année précédente">
              <ChevronLeft size={16} />
            </button>
            <div className="px-3 text-[15px] font-semibold tabular-nums text-slate-900 w-16 text-center">{year}</div>
            <button onClick={() => setYear((y) => y + 1)} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600" title="Année suivante">
              <ChevronRight size={16} />
            </button>
          </div>
          <button onClick={() => setYear(new Date().getFullYear())} className="text-[11.5px] text-slate-500 hover:text-slate-900 font-medium">
            Année en cours
          </button>
        </div>
        <div className="text-[11.5px] text-slate-500">
          {totalUnavail > 0
            ? <><strong className="text-slate-900 tabular-nums">{totalUnavail}</strong> jour{totalUnavail > 1 ? 's' : ''} d'indisponibilité en {year}</>
            : `Aucune indisponibilité en ${year}`}
        </div>
      </div>

      {/* Légende */}
      <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 mb-5">
        <div className="text-[10px] uppercase tracking-wider font-medium text-slate-500 mb-2">Affichage</div>
        <div className="flex flex-wrap gap-x-4 gap-y-2 text-[11px] text-slate-700 items-center">
          <span className="flex items-center gap-1.5">
            <span className="w-3.5 h-3.5 rounded-sm bg-amber-100 border border-amber-400"></span>Jour férié
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3.5 h-3.5 rounded-sm border border-slate-300" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #e2e8f0 0 3px, #f8fafc 3px 6px)' }}></span>Repos récurrent (non travaillé dans la semaine type)
          </span>
          <span className="w-px h-4 bg-slate-200" />
          {REASON_ORDER.map((r) => (
            <span key={r} className="flex items-center gap-1.5">
              <span className="w-3.5 h-3.5 rounded-sm" style={{ backgroundColor: REASON_META[r].dot }}></span>
              {REASON_META[r].label}
              {counts[r] ? <span className="text-slate-400 tabular-nums">· {counts[r]}</span> : null}
            </span>
          ))}
          <span className="w-px h-4 bg-slate-200" />
          <span className="flex items-center gap-1.5">
            <span className="w-3.5 h-3.5 rounded-sm bg-red-200 border border-red-400 relative"><span className="absolute top-0 left-0 w-1 h-1 rounded-full bg-red-700 opacity-70" /></span>Arrêt / AT
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3.5 h-3.5 rounded-sm bg-slate-300 border border-slate-400"></span>Congé
          </span>
          <span className="text-[10px] text-slate-400">(absence RH · point en coin)</span>
        </div>
      </div>

      {seedMut.isPending && (
        <div className="mb-4 text-[11.5px] text-slate-500 flex items-center gap-2">
          <RefreshCw size={12} className="animate-spin" /> Ajout des jours fériés {year}…
        </div>
      )}
      {(setMut.error || deleteMut.error || deleteAbsenceMut.error) && (
        <div className="mb-4 bg-danger-bg border border-danger/30 rounded-lg px-3 py-2 text-[12px] text-danger flex items-center gap-2">
          <AlertCircle size={13} /> {((setMut.error || deleteMut.error || deleteAbsenceMut.error) as Error).message}
        </div>
      )}

      {isLoading ? (
        <div className="text-sm text-slate-500 py-8 text-center">Chargement du calendrier…</div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {MONTH_NAMES.map((name, m) => (
            <MonthCard
              key={m}
              year={year}
              month={m}
              monthName={name}
              restWeekdays={restWeekdays}
              holidayMap={holidayMap}
              unavailMap={unavailMap}
              absenceMap={absenceMap}
              onDayClick={openPicker}
            />
          ))}
        </div>
      )}

      <div className="mt-5 p-3 bg-slate-50 border border-slate-200 rounded-lg text-[11px] text-slate-600 flex items-start gap-2">
        <AlertCircle size={13} className="text-slate-400 mt-0.5 flex-shrink-0" />
        <div>
          Cliquez sur un jour pour le rendre <strong>indisponible</strong> (avec un motif) — toute la journée, ou sur une <strong>plage horaire</strong> précise (dans ce cas la génération retire seulement ces heures).
          Les <strong>jours et horaires habituels</strong> se règlent dans l'onglet « Horaires & contraintes » (semaine type). Les <strong>jours fériés</strong> se gèrent dans les Paramètres de la pharmacie.
        </div>
      </div>

      {/* Popover de sélection du motif */}
      {picker && (
        <div className="fixed inset-0 z-50" onClick={() => setPicker(null)}>
          <div
            className="absolute bg-white rounded-xl shadow-xl border border-slate-200 w-[248px] overflow-y-auto overscroll-contain"
            style={{ left: picker.x, top: picker.y, maxHeight: 'calc(100vh - 24px)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-3 py-2.5 border-b border-slate-100">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">{picker.absence?.isRange ? picker.absence.label : 'Indisponible le'}</div>
              <div className="text-[12.5px] font-semibold text-slate-900 capitalize">{picker.label}</div>
              <button
                type="button"
                onClick={() => { setWeekPreview(picker.dateStr); setPicker(null); }}
                className="mt-1.5 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg border border-slate-200 text-[11.5px] font-medium text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors"
              >
                <CalendarRange size={13} className="flex-shrink-0" />
                Voir la semaine
              </button>
            </div>

            {picker.absence?.isRange ? (
              /* Jour couvert par une absence-période (congé, arrêt…) : on propose
                 de retirer TOUTE la période, depuis n'importe lequel de ses jours. */
              <div className="p-3 space-y-2.5">
                <div className="text-[11.5px] text-slate-600">
                  Ce jour fait partie d'une absence : <strong>{picker.absence.label}</strong>
                  <div className="text-slate-500 tabular-nums mt-0.5">du {fmtDate(picker.absence.from)} au {fmtDate(picker.absence.to)}</div>
                </div>
                <button
                  onClick={removeAbsence}
                  disabled={deleteAbsenceMut.isPending}
                  className="w-full flex items-center justify-center gap-2 px-2.5 py-2 rounded-lg text-[12.5px] font-medium text-white bg-danger hover:opacity-90 disabled:opacity-60 transition-colors"
                >
                  <Trash2 size={13} className="flex-shrink-0" /> Retirer cette absence
                </button>
                <div className="text-[10.5px] text-slate-400">Toute la période sera supprimée. Gérable aussi dans « Absences &amp; congés » en haut de la page.</div>
              </div>
            ) : (
              <>
            {/* Journée entière vs plage horaire */}
            <div className="px-3 pt-2.5 pb-1">
              <div className="grid grid-cols-2 gap-1 bg-slate-100 rounded-lg p-0.5">
                <button
                  type="button"
                  onClick={() => setRangeMode(false)}
                  className={`py-1.5 rounded-md text-[11.5px] font-medium transition-colors ${!rangeMode ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Journée entière
                </button>
                <button
                  type="button"
                  onClick={() => setRangeMode(true)}
                  className={`py-1.5 rounded-md text-[11.5px] font-medium transition-colors ${rangeMode ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Plage horaire
                </button>
              </div>
              {rangeMode && (
                <div className="mt-2">
                  <div className="grid grid-cols-2 gap-2">
                    <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="input tabular-nums text-center !py-1.5" />
                    <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="input tabular-nums text-center !py-1.5" />
                  </div>
                  {startTime >= endTime && (
                    <div className="text-[10.5px] text-danger mt-1">La fin doit être après le début.</div>
                  )}
                </div>
              )}
            </div>

            <div className="px-3 pt-1 pb-0.5 text-[10px] uppercase tracking-wider text-slate-400 font-medium">Motif</div>
            <div className="p-1.5 pt-0.5">
              {REASON_ORDER.map((r) => {
                const active = picker.existing?.type === r;
                return (
                  <button
                    key={r}
                    onClick={() => applyReason(r)}
                    disabled={setMut.isPending}
                    className={`w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-left text-[12.5px] transition-colors ${active ? 'bg-slate-100 font-semibold' : 'hover:bg-slate-50'}`}
                  >
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: REASON_META[r].dot }} />
                    <span className="flex-1 text-slate-700">{REASON_META[r].label}</span>
                    {active && <Check size={13} className="text-slate-500" />}
                  </button>
                );
              })}
            </div>
            {picker.existing && (
              <div className="p-1.5 border-t border-slate-100">
                <button
                  onClick={removeUnavailability}
                  disabled={deleteMut.isPending}
                  className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-left text-[12.5px] text-danger hover:bg-danger-bg transition-colors"
                >
                  <Trash2 size={13} className="flex-shrink-0" />
                  Retirer l'indisponibilité
                </button>
              </div>
            )}
              </>
            )}
          </div>
        </div>
      )}

      {weekPreview && (
        <RequestWeekPreview date={weekPreview} onClose={() => setWeekPreview(null)} />
      )}
    </>
  );
}

// ============================================================================
// ABSENCES & CONGÉS RH — période + type (source de vérité, planning auto)
// ============================================================================

// Liste unifiée (11 types) + couleurs, dérivée de la source unique.
const ABSENCE_TYPE_OPTIONS = ABSENCE_TYPES.map((v) => ({ v, l: ABSENCE_META[v].label, cls: ABSENCE_META[v].chip }));
const ABSENCE_LABEL = Object.fromEntries(ABSENCE_TYPES.map((v) => [v, ABSENCE_META[v].label])) as Record<AbsenceType, string>;
const ABSENCE_CLS = Object.fromEntries(ABSENCE_TYPES.map((v) => [v, ABSENCE_META[v].chip])) as Record<AbsenceType, string>;
const ABSENCE_CAL_CLS = Object.fromEntries(ABSENCE_TYPES.map((v) => [v, ABSENCE_META[v].cell])) as Record<AbsenceType, string>;
const fmtDate = (iso: string) => { const [y, m, d] = iso.slice(0, 10).split('-'); return `${d}/${m}/${y}`; };

function AbsencesSection({ employeeId, year }: { employeeId: string; year: number }) {
  const { data: allAbsences } = useAbsences();
  const createMut = useCreateAbsence();
  const deleteMut = useDeleteAbsence();
  const [adding, setAdding] = useState(false);
  const [type, setType] = useState<AbsenceType>('CONGE_PAYE');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [comment, setComment] = useState('');

  const absences = useMemo(() =>
    (allAbsences ?? [])
      .filter((a) => a.employeeId === employeeId)
      .filter((a) => new Date(a.startDate).getFullYear() <= year && new Date(a.endDate).getFullYear() >= year)
      .sort((a, b) => a.startDate.localeCompare(b.startDate)),
    [allAbsences, employeeId, year]);

  async function handleAdd() {
    if (!startDate || !endDate) return;
    await createMut.mutateAsync({ employeeId, type, startDate, endDate, comment: comment || undefined });
    setAdding(false); setComment(''); setStartDate(''); setEndDate('');
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[13px] font-semibold text-slate-900">Absences & congés</div>
          <div className="text-[11px] text-slate-500">Sélectionnez une <strong className="font-semibold text-slate-600">période</strong> (début → fin) — plus rapide que jour par jour. Le planning et la fiche navette se mettent à jour automatiquement.</div>
        </div>
        {!adding && <Button variant="default" size="sm" icon={Plus} onClick={() => setAdding(true)}>Ajouter une période</Button>}
      </div>

      {adding && (
        <div className="border border-slate-200 rounded-lg p-3 mb-3 bg-slate-50/50 space-y-3">
          <div>
            <label className="label">Type</label>
            <div className="grid grid-cols-3 gap-1.5">
              {ABSENCE_TYPE_OPTIONS.map((o) => (
                <button key={o.v} type="button" onClick={() => setType(o.v)} className={`py-1.5 px-2 rounded-lg border text-[11.5px] font-medium ${type === o.v ? 'bg-primary text-white border-primary' : 'bg-white border-slate-200 text-slate-700 hover:border-slate-400'}`}>{o.l}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Début</label><input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="input" /></div>
            <div><label className="label">Fin</label><input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="input" /></div>
          </div>
          {type === 'CONGE_PAYE' && (
            <div className="text-[11px] text-slate-500 -mt-1">Le décompte suit les jours d'ouverture : une semaine complète compte <strong className="font-semibold text-slate-600">6 jours</strong> de CP (jour de repos inclus, dimanche et fériés fermés exclus).</div>
          )}
          <div><label className="label">Commentaire <span className="text-slate-400 font-normal">(facultatif)</span></label><input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Justificatif ? Remplaçant ?" className="input" /></div>
          {createMut.error && <div className="text-[11.5px] text-danger">{(createMut.error as Error).message}</div>}
          <div className="flex justify-end gap-2">
            <Button variant="default" size="sm" onClick={() => setAdding(false)}>Annuler</Button>
            <Button variant="primary" size="sm" loading={createMut.isPending} disabled={!startDate || !endDate} onClick={handleAdd}>Créer</Button>
          </div>
        </div>
      )}

      {absences.length === 0 ? (
        <div className="text-[11.5px] text-slate-400 py-1">Aucune absence en {year}.</div>
      ) : (
        <div className="space-y-1.5">
          {absences.map((a) => (
            <div key={a.id} className="flex items-center gap-2 text-[12px] py-1.5 px-2 rounded-lg hover:bg-slate-50">
              <span className={`px-2 py-0.5 rounded text-[10.5px] font-medium ${ABSENCE_CLS[a.type]}`}>{ABSENCE_LABEL[a.type]}</span>
              <span className="tabular-nums text-slate-700">du <strong>{fmtDate(a.startDate)}</strong> au <strong>{fmtDate(a.endDate)}</strong></span>
              {a.comment && <span className="text-slate-400 truncate">· {a.comment}</span>}
              <button onClick={() => { if (window.confirm('Supprimer cette absence ?')) deleteMut.mutate(a.id); }} className="ml-auto p-1 rounded hover:bg-danger-bg text-slate-400 hover:text-danger" title="Supprimer"><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MonthCard({
  year, month, monthName, restWeekdays, holidayMap, unavailMap, absenceMap, onDayClick,
}: {
  year: number;
  month: number;
  monthName: string;
  restWeekdays: number[];
  holidayMap: Map<string, { name: string; isOpen: boolean }>;
  unavailMap: Map<string, UnavailInfo>;
  absenceMap: Map<string, AbsenceDayInfo>;
  onDayClick: (dateStr: string, label: string, e: React.MouseEvent) => void;
}) {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstWeekday = new Date(year, month, 1).getDay(); // 0=dim
  const offset = (firstWeekday + 6) % 7; // lundi=0
  const today = new Date();
  const todayKey = dateKey(today.getFullYear(), today.getMonth(), today.getDate());

  const cells: (number | null)[] = [
    ...Array.from({ length: offset }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <div className="text-[12.5px] font-semibold text-slate-900 mb-2 px-0.5">{monthName}</div>
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {WEEKDAY_HEADERS.map((h, i) => (
          <div key={i} className="text-[9px] text-slate-400 text-center font-medium uppercase">{h}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((day, idx) => {
          if (day === null) return <div key={idx} />;
          const dStr = dateKey(year, month, day);
          const weekday = new Date(year, month, day).getDay();
          const holiday = holidayMap.get(dStr);
          const unavail = unavailMap.get(dStr);
          const absence = absenceMap.get(dStr);
          const isFixedOff = restWeekdays.includes(weekday);
          const isToday = dStr === todayKey;
          const dayLabel = new Date(year, month, day).toLocaleDateString('fr-FR', {
            weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
          });

          // Priorité d'affichage : indispo ponctuelle (bloque) > férié (info) > repos fixe.
          // Tous les jours restent cliquables : liberté totale, un férié n'est PAS
          // une indisponibilité (la pharmacie peut ouvrir), on peut donc y ajouter/retirer une indispo.
          let cls = 'bg-white border-transparent text-slate-700 hover:border-slate-300';
          let style: React.CSSProperties | undefined;
          const titleParts = [dayLabel];

          const isPartial = !!(unavail?.startTime && unavail?.endTime);
          if (absence) {
            // Absence RH (arrêt, congé…) — prioritaire à l'affichage.
            cls = `${ABSENCE_CAL_CLS[absence.type]} hover:opacity-90`;
            titleParts.push(absence.label);
            if (unavail) titleParts.push(REASON_META[unavail.type].label);
            if (holiday) titleParts.push(`férié : ${holiday.name}`);
          } else if (unavail) {
            cls = REASON_META[unavail.type].cell;
            titleParts.push(
              isPartial
                ? `${REASON_META[unavail.type].label} · ${unavail.startTime}–${unavail.endTime}`
                : REASON_META[unavail.type].label
            );
            if (holiday) titleParts.push(`férié : ${holiday.name}`);
          } else if (holiday) {
            cls = 'bg-amber-100 text-amber-700 border-amber-300 hover:border-amber-500';
            titleParts.push(`férié : ${holiday.name}`);
          } else if (isFixedOff) {
            cls = 'text-slate-500 border-slate-200 hover:border-slate-400';
            style = { backgroundImage: 'repeating-linear-gradient(45deg, #e2e8f0 0 3px, #f8fafc 3px 6px)' };
            titleParts.push('repos récurrent (semaine type)');
          }

          return (
            <button
              key={idx}
              type="button"
              onClick={(e) => onDayClick(dStr, dayLabel, e)}
              title={titleParts.join(' — ')}
              style={style}
              className={`relative aspect-square rounded-md border text-[10.5px] font-medium tabular-nums flex items-center justify-center transition-colors ${cls} ${isToday ? 'ring-1 ring-primary ring-offset-1' : ''}`}
            >
              {day}
              {absence && (
                <span className="absolute top-0.5 left-0.5 w-1.5 h-1.5 rounded-full bg-current opacity-70" />
              )}
              {holiday && (unavail || absence) && (
                <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-amber-400" />
              )}
              {isPartial && !absence && (
                <span className="absolute bottom-[3px] left-1.5 right-1.5 h-[2px] rounded-full bg-current opacity-60" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SkillsTab({ form, setForm }: { form: any; setForm: (f: any) => void }) {
  // Prestations ACTIVES du catalogue : la liste suit le catalogue en direct.
  // Ajouter ou désactiver une prestation la fait apparaître / disparaître ici.
  const { data: catalog } = useAppointmentCatalog();

  const toggle = (skill: Skill) => {
    const s = new Set<Skill>(form.skills);
    if (s.has(skill)) s.delete(skill);
    else s.add(skill);
    setForm({ ...form, skills: [...s] });
  };

  const catalogSkills: string[] = form.catalogSkills ?? [];
  const toggleCatalog = (id: string) => {
    const s = new Set<string>(catalogSkills);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    setForm({ ...form, catalogSkills: [...s] });
  };

  // Regroupement par catégorie, dans l'ordre du catalogue.
  const byCategory = useMemo(() => {
    const groups = new Map<string, AppointmentCatalogItem[]>();
    for (const item of catalog ?? []) {
      const key = item.category ?? 'Autres prestations';
      const list = groups.get(key) ?? [];
      list.push(item);
      groups.set(key, list);
    }
    return [...groups.entries()];
  }, [catalog]);

  const setAllInCategory = (items: AppointmentCatalogItem[], on: boolean) => {
    const s = new Set<string>(catalogSkills);
    for (const it of items) {
      if (on) s.add(it.id);
      else s.delete(it.id);
    }
    setForm({ ...form, catalogSkills: [...s] });
  };

  return (
    <Section
      title="Compétences et habilitations"
      description="Les compétences pilotent les missions du planning. Les prestations, elles, se cochent une par une : être habilité aux entretiens ne veut pas dire les faire tous."
    >
      {/* Regroupées par famille (Soins / Technique / Accueil) : 10 cases à plat
          étaient illisibles. Le compteur par famille donne l'essentiel d'un coup d'œil. */}
      <div className="space-y-5">
        {SKILL_FAMILIES.map((family) => {
          const owned = family.skills.filter((s) => form.skills.includes(s)).length;
          return (
            <div key={family.id}>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: family.dot }} />
                <span className="text-[12.5px] font-semibold text-slate-900">{family.label}</span>
                <span className="text-[10.5px] text-slate-500">{family.description}</span>
                <span className="ml-auto text-[10.5px] text-slate-400 tabular-nums">
                  {owned}/{family.skills.length}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {family.skills.map((k) => {
                  const active = form.skills.includes(k);
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => toggle(k)}
                      className={`flex items-start gap-2.5 p-3 rounded-xl border text-left transition-all ${
                        active ? 'border-primary bg-primary-bg/40' : 'border-slate-200 bg-white hover:border-slate-400'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        active ? 'border-primary bg-primary' : 'border-slate-300 bg-white'
                      }`}>
                        {active && <Check size={10} className="text-white" strokeWidth={3} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-[12.5px] font-semibold ${active ? 'text-primary' : 'text-slate-900'}`}>{SKILL_LABELS[k]}</div>
                        <div className="text-[10.5px] text-slate-500 mt-0.5">{SKILL_DESCRIPTIONS[k]}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Habilitations PRESTATION PAR PRESTATION                             */}
      {/* ------------------------------------------------------------------ */}
      <div className="mt-8 pt-6 border-t border-slate-200">
        <div className="flex items-baseline gap-2 mb-1">
          <span className="text-[13px] font-semibold text-slate-900">Prestations sur rendez-vous</span>
          <span className="ml-auto text-[11px] text-slate-500 tabular-nums">
            {catalogSkills.length} habilitation{catalogSkills.length > 1 ? 's' : ''}
          </span>
        </div>
        <p className="text-[11.5px] text-slate-500 mb-4 leading-relaxed">
          Seules les prestations cochées ici pourront lui être attribuées à la prise de rendez-vous.
          La liste suit le catalogue : une prestation ajoutée y apparaît aussitôt, une prestation
          désactivée en disparaît.
        </p>

        {byCategory.length === 0 ? (
          <div className="text-[12px] text-slate-500 bg-slate-50 border border-slate-200 rounded-xl p-4">
            Aucune prestation active dans le catalogue. Ajoutez-en depuis
            <strong> Paramètres › Prestations sur rendez-vous</strong>.
          </div>
        ) : (
          <div className="space-y-5">
            {byCategory.map(([category, items]) => {
              const owned = items.filter((i) => catalogSkills.includes(i.id)).length;
              const allOn = owned === items.length;
              return (
                <div key={category}>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-[12.5px] font-semibold text-slate-900">{category}</span>
                    <button
                      type="button"
                      onClick={() => setAllInCategory(items, !allOn)}
                      className="text-[10.5px] text-primary hover:underline"
                    >
                      {allOn ? 'Tout décocher' : 'Tout cocher'}
                    </button>
                    <span className="ml-auto text-[10.5px] text-slate-400 tabular-nums">
                      {owned}/{items.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {items.map((item) => {
                      const active = catalogSkills.includes(item.id);
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => toggleCatalog(item.id)}
                          className={`flex items-start gap-2.5 p-3 rounded-xl border text-left transition-all ${
                            active ? 'border-primary bg-primary-bg/40' : 'border-slate-200 bg-white hover:border-slate-400'
                          }`}
                        >
                          <div
                            className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                              active ? 'border-primary bg-primary' : 'border-slate-300 bg-white'
                            }`}
                          >
                            {active && <Check size={10} className="text-white" strokeWidth={3} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={`text-[12.5px] font-semibold ${active ? 'text-primary' : 'text-slate-900'}`}>
                              {item.label}
                            </div>
                            <div className="text-[10.5px] text-slate-500 mt-0.5">
                              {item.defaultDurationMinutes} min
                              {item.defaultEmployeeId ? ' · prestation nominative' : ''}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Section>
  );
}

function RotationTab({ form, setForm }: { form: any; setForm: (f: any) => void }) {
  const set = (v: RotationCycleType, weeks?: number) =>
    setForm({ ...form, rotationCycle: { ...form.rotationCycle, cycleType: v, cycleWeeks: weeks ?? form.rotationCycle.cycleWeeks } });

  const options: { v: RotationCycleType; l: string; d: string }[] = [
    { v: 'NONE', l: 'Pas de roulement', d: 'Horaires définis sans logique cyclique' },
    { v: 'PAIRE_IMPAIRE', l: 'Semaines paires / impaires', d: 'Alternance 2 semaines différentes' },
    { v: 'WEEKS_3', l: 'Cycle de 3 semaines', d: 'Roulement sur 3 semaines' },
    { v: 'WEEKS_4', l: 'Cycle de 4 semaines', d: 'Roulement sur 4 semaines' },
    { v: 'WEEKS_N', l: 'Personnalisé', d: 'Nombre de semaines à définir' },
  ];

  return (
    <Section title="Cycle de roulement" description="Type de répétition des horaires hebdomadaires.">
      <div className="space-y-2">
        {options.map((opt) => (
          <button
            key={opt.v}
            type="button"
            onClick={() => set(opt.v)}
            className={`w-full flex items-center gap-3 p-3 border rounded-xl text-left transition-all ${
              form.rotationCycle.cycleType === opt.v ? 'border-primary bg-primary-bg/40 ring-2 ring-primary/10' : 'border-slate-200 bg-white hover:border-slate-400'
            }`}
          >
            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
              form.rotationCycle.cycleType === opt.v ? 'border-primary' : 'border-slate-300'
            }`}>
              {form.rotationCycle.cycleType === opt.v && <div className="w-2 h-2 rounded-full bg-primary" />}
            </div>
            <div className="flex-1">
              <div className="text-[13px] font-semibold text-slate-900">{opt.l}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{opt.d}</div>
            </div>
          </button>
        ))}
      </div>

      {form.rotationCycle.cycleType === 'WEEKS_N' && (
        <div className="mt-4">
          <label className="label">Nombre de semaines du cycle</label>
          <input
            type="number"
            min={2}
            max={8}
            value={form.rotationCycle.cycleWeeks ?? ''}
            onChange={(e) => setForm({ ...form, rotationCycle: { ...form.rotationCycle, cycleWeeks: parseInt(e.target.value) || null } })}
            className="input w-40"
          />
        </div>
      )}
    </Section>
  );
}

function CountersTab({ employeeId, employee }: { employeeId: string; employee: Employee }) {
  const { data: counters, isLoading, error } = useEmployeeCounters(employeeId);

  if (isLoading) return <div className="text-sm text-slate-500">Chargement des compteurs…</div>;
  if (error) return <div className="text-sm text-danger">Erreur : {(error as Error).message}</div>;
  if (!counters) return null;

  const now = new Date();
  const monthLabel = now.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const yearLabel = String(now.getFullYear());

  return (
    <>
      <HoursBalanceManager employeeId={employeeId} balance={counters.hoursBalance ?? 0} />

      <Section title={`Heures de ${monthLabel}`} description="Heures planifiées dans le mois en cours.">
        <div className="grid grid-cols-3 gap-4">
          <Counter
            label="Heures planifiées"
            value={`${counters.month.plannedHours.toFixed(1)}h`}
            caption={`sur ${counters.month.contractHours.toFixed(0)}h contractuelles`}
            variant="primary"
          />
          <Counter
            label="Heures supplémentaires"
            value={`+${counters.month.overtimeHours.toFixed(1)}h`}
            caption={counters.month.overtimeHours > 0 ? 'à récupérer ou rémunérer' : 'aucune ce mois'}
            variant={counters.month.overtimeHours > 0 ? 'amber' : 'default'}
          />
          <Counter
            label="Ratio"
            value={`${Math.round((counters.month.plannedHours / counters.month.contractHours) * 100)}%`}
            caption="du temps contractuel"
            variant="default"
          />
        </div>
      </Section>

      <Section title={`Congés ${yearLabel}`} description="Solde annuel de congés payés (25 jours par défaut).">
        <div className="grid grid-cols-3 gap-4">
          <Counter
            label="Quota annuel"
            value={`${counters.leaves.quotaYearly}j`}
            caption="congés payés"
            variant="default"
          />
          <Counter
            label="Pris"
            value={`${counters.leaves.takenYtd}j`}
            caption="depuis janvier"
            variant="primary"
          />
          <Counter
            label="Restant"
            value={`${counters.leaves.remaining}j`}
            caption="jusqu'à décembre"
            variant={counters.leaves.remaining < 5 ? 'amber' : 'default'}
          />
        </div>
      </Section>

      <Section title="Santé" description="Sur les 12 derniers mois glissants.">
        <div className="grid grid-cols-3 gap-4">
          <Counter
            label="Jours d'arrêt"
            value={`${counters.sickLast12Months}j`}
            caption="maladie + accident travail"
            variant={counters.sickLast12Months > 15 ? 'danger' : 'default'}
          />
        </div>
      </Section>
    </>
  );
}

function HoursBalanceManager({ employeeId, balance }: { employeeId: string; balance: number }) {
  const { data, isLoading } = useHourAdjustments(employeeId);
  const addMut = useAddHourAdjustment(employeeId);
  const delMut = useDeleteHourAdjustment(employeeId);

  const [sign, setSign] = useState<1 | -1>(1);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const adjustments = data?.adjustments ?? [];
  const total = data?.total ?? balance;

  async function add() {
    const val = parseFloat(amount.replace(',', '.'));
    if (!val || val <= 0) return;
    await addMut.mutateAsync({ hours: sign * val, reason: reason.trim() || null });
    setAmount('');
    setReason('');
  }

  const fmt = (h: number) => `${h > 0 ? '+' : ''}${h.toFixed(1).replace('.0', '')}h`;

  return (
    <Section
      title="Solde d'heures"
      description="Heures dues ou en trop, ajustables manuellement (dont le solde initial à l'installation)."
    >
      {/* Total */}
      <div className={`border rounded-xl p-4 mb-4 flex items-center justify-between ${
        total > 0 ? 'bg-primary-bg/60 border-primary/25' : total < 0 ? 'bg-danger-bg border-danger/30' : 'bg-slate-50 border-slate-200'
      }`}>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1">Solde cumulé</div>
          <div className={`text-[28px] font-semibold tabular-nums leading-none ${
            total > 0 ? 'text-primary' : total < 0 ? 'text-danger' : 'text-slate-900'
          }`}>{fmt(total)}</div>
        </div>
        <div className="text-[11px] text-slate-500 text-right max-w-[180px]">
          {total > 0 ? 'Heures dues à l\'employé (crédit)' : total < 0 ? 'Heures à rattraper par l\'employé' : 'Aucun écart enregistré'}
        </div>
      </div>

      {/* Formulaire d'ajout */}
      <div className="border border-slate-200 rounded-xl p-3.5 mb-4 bg-white">
        <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium mb-2">Ajouter un ajustement</div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex rounded-lg overflow-hidden border border-slate-300">
            <button type="button" onClick={() => setSign(1)} className={`px-3 py-1.5 text-[13px] font-semibold ${sign === 1 ? 'bg-primary text-white' : 'bg-white text-slate-500'}`}>+</button>
            <button type="button" onClick={() => setSign(-1)} className={`px-3 py-1.5 text-[13px] font-semibold ${sign === -1 ? 'bg-danger text-white' : 'bg-white text-slate-500'}`}>−</button>
          </div>
          <div>
            <input
              type="number" step="0.5" min="0" inputMode="decimal"
              value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder="Heures" className="input w-24 tabular-nums" />
          </div>
          <input
            value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Motif (ex. solde initial installation)" className="input flex-1 min-w-[180px]" />
          <Button variant="primary" icon={Plus} loading={addMut.isPending} disabled={!amount} onClick={add}>
            Ajouter
          </Button>
        </div>
        {addMut.error && <div className="text-[11.5px] text-danger mt-2">{(addMut.error as Error).message}</div>}
      </div>

      {/* Liste */}
      {isLoading ? (
        <div className="text-[12px] text-slate-400">Chargement…</div>
      ) : adjustments.length === 0 ? (
        <div className="text-[12px] text-slate-400 italic">Aucun ajustement enregistré.</div>
      ) : (
        <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
          {adjustments.map((a) => (
            <div key={a.id} className="flex items-center gap-3 px-3.5 py-2.5">
              <span className={`text-[14px] font-semibold tabular-nums w-14 ${a.hours >= 0 ? 'text-primary' : 'text-danger'}`}>{fmt(a.hours)}</span>
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] text-slate-800 truncate">{a.reason || 'Ajustement manuel'}</div>
                <div className="text-[10.5px] text-slate-400">
                  {new Date(a.effectiveDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </div>
              </div>
              <button
                onClick={() => delMut.mutate(a.id)}
                disabled={delMut.isPending}
                className="p-1.5 hover:bg-danger-bg rounded-lg text-slate-400 hover:text-danger"
                title="Supprimer"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function Counter({
  label, value, caption, variant = 'default',
}: {
  label: string;
  value: string;
  caption: string;
  variant?: 'default' | 'primary' | 'amber' | 'danger';
}) {
  const colors = {
    default: 'bg-slate-50 border-slate-200',
    primary: 'bg-primary-bg/60 border-primary/25',
    amber: 'bg-[#fcf1e2]/60 border-[#b8741e]/30',
    danger: 'bg-danger-bg border-danger/30',
  };
  const valueColors = {
    default: 'text-slate-900',
    primary: 'text-primary',
    amber: 'text-[#b8741e]',
    danger: 'text-danger',
  };
  return (
    <div className={`border rounded-xl p-4 ${colors[variant]}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1">{label}</div>
      <div className={`text-[26px] font-semibold tabular-nums tracking-tight leading-none ${valueColors[variant]}`}>{value}</div>
      <div className="text-[10.5px] text-slate-500 mt-1">{caption}</div>
    </div>
  );
}

function HistoryTab({ employee }: { employee: Employee }) {
  const { data: events = [], isLoading } = useEmployeeHistory(employee.id);
  return (
    <Section title="Historique" description="Événements enregistrés pour ce salarié.">
      <div className="space-y-3">
        <div className="flex items-start gap-3 py-2">
          <div className="w-7 h-7 rounded-full bg-primary-bg flex items-center justify-center flex-shrink-0">
            <Calendar size={13} className="text-primary" />
          </div>
          <div>
            <div className="text-[12.5px] font-medium text-slate-900">Embauche</div>
            <div className="text-[11px] text-slate-500">
              {employee.hireDate ? new Date(employee.hireDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
            </div>
          </div>
        </div>
        {employee.endDate && (
          <div className="flex items-start gap-3 py-2">
            <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
              <Calendar size={13} className="text-slate-500" />
            </div>
            <div>
              <div className="text-[12.5px] font-medium text-slate-900">Fin de contrat prévue</div>
              <div className="text-[11px] text-slate-500">
                {new Date(employee.endDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
            </div>
          </div>
        )}
        {employee.archived && (
          <div className="flex items-start gap-3 py-2">
            <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
              <Archive size={13} className="text-slate-500" />
            </div>
            <div>
              <div className="text-[12.5px] font-medium text-slate-900">Salarié archivé</div>
              <div className="text-[11px] text-slate-500">Masqué du planning actif</div>
            </div>
          </div>
        )}

        {events.map((ev) => (
          <div key={ev.id} className="flex items-start gap-3 py-2">
            <div className="w-7 h-7 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
              <RefreshCw size={13} className="text-accent" />
            </div>
            <div className="min-w-0">
              <div className="text-[12.5px] font-medium text-slate-900">{ev.title}</div>
              <div className="text-[11.5px] text-slate-600">{ev.message}</div>
              <div className="text-[10.5px] text-slate-400 mt-0.5">
                {new Date(ev.createdAt).toLocaleString('fr-FR', {
                  day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
                })}
              </div>
            </div>
          </div>
        ))}
      </div>

      {isLoading && <div className="mt-3 text-[11px] text-slate-400">Chargement de l'historique…</div>}
    </Section>
  );
}

// ============================================================================
// MODALE D'INVITATION — affiche le code à transmettre au salarié
// ============================================================================
function InviteModal({ invite, onClose }: { invite: EmployeeInvite; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const expires = new Date(invite.expiresAt).toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  async function copy() {
    try {
      await navigator.clipboard.writeText(invite.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard indisponible — l'utilisateur peut recopier à la main */
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"
          title="Fermer"
        >
          <X size={16} />
        </button>

        <div className="flex items-center gap-2.5 mb-1">
          <div className="w-8 h-8 rounded-lg bg-primary-bg flex items-center justify-center">
            <KeyRound size={16} className="text-primary" />
          </div>
          <h2 className="text-[15px] font-semibold text-slate-900">Code d'invitation</h2>
        </div>
        <p className="text-[12px] text-slate-500 mb-4">
          Transmettez ce code à {invite.employee.firstName} {invite.employee.lastName}
          {' '}(SMS, WhatsApp, oral…). Il l'utilisera pour activer son application mobile.
        </p>

        <div className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
          <span className="text-[26px] font-bold tracking-[0.3em] text-slate-900 font-mono">
            {invite.code}
          </span>
          <Button variant="default" size="sm" icon={copied ? Check : Copy} onClick={copy}>
            {copied ? 'Copié' : 'Copier'}
          </Button>
        </div>

        <p className="text-[11px] text-slate-400 mt-3">
          À usage unique · expire le {expires}
        </p>
      </div>
    </div>
  );
}
